"use client";

import Image from "next/image";
import React, { useContext, useState } from "react";
import {
  Menu,
  X,
  Home,
  LogOut,
  Users,
  Box,
  Tablet,
  ChevronDown,
  LampDesk,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import MobileNav from "../MobileNav";
import { twMerge } from "tailwind-merge";
import { CMSModal } from "@/context";
import ChangePasswordModal from "./ChangePasswordModal";
import ProfileDropdown from "./ProfileDropdown";

import { useTranslation } from "react-i18next";
import { Dropdown } from "antd";
import { User, Building2, Shield, Plug, FileText, MapPin } from "lucide-react";

const locationItems = [
  {
    label: "Visitor Settings",
    description: "Customize your visitor sign-in flow",
    icon: LogOut,
    path: "/settings/visitor/fields",
  },
  {
    label: "Employee Settings",
    description: "Manage employee sign-in & profile",
    icon: Users,
    path: "/settings/employee/general",
  },
  {
    label: "Deliveries Settings",
    description: "Configure delivery messaging and contacts",
    icon: Box,
    path: "/settings/deliveries/setup",
  },
  {
    label: "Spaces Settings",
    description: "Manage your bookable resources",
    icon: LampDesk,
    path: "/settings/space/space",
  },
  {
    label: "Devices",
    description: "Set up for devices",
    icon: Tablet,
    path: "/settings/device",
  },
];

const orgItems = [
  {
    label: "Agreements Library",
    description: "Manage company agreements",
    icon: FileText,
    path: "/settings/agreement-library",
  },
  {
    label: "Locations & AddOns",
    description: "Manage locations & QR codes",
    icon: MapPin,
    path: "/settings/locations",
  },
  {
    label: "Integrations",
    description: "Connect external tools",
    icon: Plug,
    path: "/settings/integrations",
  },
];
const navLinks = [
  { name: "visitors", link: "/visitors/timeline", match: "/visitors" },
  { name: "employees", link: "/employees/timeline", match: "/employees" },
  { name: "spaces", link: "/spaces/bookings", match: "/spaces" },
  { name: "deliveries", link: "/delivery", match: "/delivery" },
  { name: "settings", link: "/settings/visitor/fields", match: "/settings" },
];

const Header = () => {
  const { t } = useTranslation();
  const { user, theme } = useContext(CMSModal);
  const [mobileOpen, setMobileOpen] = useState(false);
  // dropdownOpen moved to ProfileDropdown component
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const pathname = usePathname();
  const router = useRouter();
  return (
    <header
      className={twMerge(
        "shadow w-full z-32 h-18 lg:h-20",
        !mobileOpen ? "" : "",
        theme === "dark" ? "bg-[#262727] text-gray-100" : "bg-white",
      )}
    >
      <div className="w-full flex items-center justify-between px-6 h-full">
        <div className="flex items-center gap-8 h-full">
          <Link href="/" className="flex items-center">
            {/* Mobile */}
            <div className="block lg:hidden flex items-center justify-center group relative">
              <Image
                src={theme === "dark" ? "/IconWt.png" : "/Icon.png"}
                alt="Logo"
                width={300}
                height={40}
                className="absolute text-[#099ed1] w-6 h-6 opacity-0 scale-75 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:scale-100"
              />
              <Image
                src="/LogoGlobe.png"
                alt="Logo"
                width={40}
                height={40}
                className="w-10 h-10 object-contain transition-all duration-300 ease-out group-hover:opacity-20 group-hover:scale-95"
              />
            </div>

            {/* Desktop */}
            <div
              className={twMerge(
                "hidden md:flex items-center justify-center relative",
                pathname !== "/" && "group",
              )}
            >
              <Image
                src={theme === "dark" ? "/IconWt.png" : "/Icon.png"}
                alt="Logo"
                width={300}
                height={40}
                className="absolute text-[#099ed1] w-6 h-6 opacity-0 scale-75 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:scale-100"
              />

              <Image
                src={theme === "dark" ? "/LogoWt.png" : "/LogoBlue.png"}
                alt="Logo"
                width={300}
                height={40}
                className="md:hidden lg:block h-8 w-auto object-contain transition-all duration-300 ease-out group-hover:opacity-20 group-hover:scale-95"
              />
            </div>
          </Link>
          <nav className="hidden md:flex  items-center h-full cursor-pointer">
            {navLinks.map((item) => {
              const isActive = pathname.includes(item.match);
              if (item.name === "settings") {
                return (
                  <Dropdown
                    key={item.name}
                    placement="bottom"
                    trigger={["hover"]}
                    open={openKey === item.name}
                    onOpenChange={(val) => setOpenKey(val ? item.name : null)}
                    mouseEnterDelay={0.1}
                    mouseLeaveDelay={0.15}
                    popupRender={() => (
                      <div
                        className={twMerge(
                          "shadow-xl rounded-xl p-6 w-[720px] mt-10",
                          theme == "dark" ? "bg-[#262727]" : "bg-white",
                        )}
                      >
                        <div className={twMerge("grid grid-cols-2 gap-4")}>
                          {/* LEFT */}
                          <div>
                            <p className="text-xs text-gray-400 font-semibold mb-4">
                              LOCATION
                            </p>

                            <div className="flex flex-col gap-2">
                              {locationItems.map((item) => (
                                <div
                                  key={item.label}
                                  onClick={() => router.push(item.path)}
                                  className="group flex gap-3 cursor-pointer p-2 rounded-lg transition"
                                >
                                  <item.icon className="w-5 h-5 mt-1 text-gray-500 transition-colors group-hover:text-[#f74000]" />

                                  <div>
                                    <p
                                      className={twMerge(
                                        "font-semibold transition-colors group-hover:text-[#f74000]",
                                        theme === "dark"
                                          ? "text-gray-400"
                                          : "text-gray-800",
                                      )}
                                    >
                                      {item.label}
                                    </p>

                                    <p className="text-sm text-gray-500 transition-colors">
                                      {item.description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* RIGHT */}
                          <div>
                            <p className="text-xs text-gray-400 font-semibold mb-4">
                              ORGANIZATION
                            </p>

                            <div className="flex flex-col gap-2">
                              {orgItems.map((item) => (
                                <div
                                  key={item.label}
                                  onClick={() => router.push(item.path)}
                                  className="group flex gap-3 cursor-pointer p-2 rounded-lg transition"
                                >
                                  <item.icon className="w-5 h-5 text-gray-500 mt-1 group-hover:text-[#f74000]" />

                                  <div>
                                    <p
                                      className={twMerge(
                                        "font-semibold group-hover:text-[#f74000]",
                                        theme === "dark"
                                          ? "text-gray-400"
                                          : "text-gray-800",
                                      )}
                                    >
                                      {item.label}
                                    </p>
                                    <p className="text-gray-500 text-sm">
                                      {item.description}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  >
                    <div
                      className={twMerge(
                        "flex items-center gap-1 cursor-pointer text-sm font-semibold transition-colors px-6 h-full border-b-2",
                        theme === "dark" ? "!text-white" : "",
                        isActive || openKey === item.name
                          ? "text-[#f74000] border-[#f74000]"
                          : "text-gray-800 border-transparent",
                      )}
                    >
                      {t(`header.${item.name}`)}

                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-300 ${
                          openKey === item.name ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </Dropdown>
                );
              } else {
                return (
                  <Link
                    key={item.name}
                    href={item.link}
                    className={twMerge(
                      "relative h-full text-sm font-semibold flex items-center px-6 hover:text-[#f74000] transition-colors border-b-2",
                      isActive
                        ? "text-[#f74000] border-[#f74000]"
                        : "border-transparent",
                    )}
                  >
                    {t(`header.${item.name}`)}
                  </Link>
                );
              }
            })}
          </nav>
        </div>

        {/* Right Side (Desktop) */}
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-4 text-gray-600">
            {/* Profile Dropdown */}
            {/* Profile Dropdown */}
            <div className="hidden md:block">
              <ProfileDropdown
                user={user}
                setPasswordModalOpen={setPasswordModalOpen}
              />
            </div>
          </div>
        </div>

        {/* Mobile Right Side */}
        <div className="flex items-center md:hidden gap-2">
          <div className="md:hidden">
            <ProfileDropdown
              user={user}
              setPasswordModalOpen={setPasswordModalOpen}
            />
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="">
            {mobileOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && <MobileNav />}

      <ChangePasswordModal
        isOpen={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </header>
  );
};

export default Header;
